import { createTheme, type PaletteOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface PaletteOptions {
    blue?: { 200: string; 500: string; 600: string };
    green?: { 400: string };
  }
  interface Color {
    450: string;
  }
}

// 3CE brand palette, sampled from https://3cenergy.org/about-us/
// (--wp--preset--color--*)
//   primary    #1a4548  deep teal      — headlines, CTAs, accents
//   secondary  #ffe2c7  warm peach     — soft fills, highlights
//   tertiary   #F6F6F6  off-white      — section backgrounds
//   foreground #1a1a1a  near-black     — body text
//   background #ffffff  white          — canvas
const BRAND = {
  teal: "#1a4548",
  tealLight: "#2e6b6e",
  tealDark: "#0d2c2e",
  peach: "#ffe2c7",
  peachLight: "#fff1e3",
  peachDark: "#e6b88a",
  offWhite: "#F6F6F6",
  ink: "#1a1a1a",
  // WCAG AA: text.secondary on white needs ≥4.5:1 for body text.
  // #4a4a4a hits ~7.7:1 on white; #5a5a5a was ~3.8:1.
  inkMuted: "#4a4a4a",
};

const theme = createTheme({
  typography: {
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: '"Source Serif Pro", Georgia, serif', fontWeight: 300 },
    h2: { fontFamily: '"Source Serif Pro", Georgia, serif', fontWeight: 300 },
    h3: { fontFamily: '"Source Serif Pro", Georgia, serif', fontWeight: 300 },
    h4: { fontFamily: '"Source Serif Pro", Georgia, serif', fontWeight: 300 },
  },
  palette: {
    primary: {
      main: BRAND.teal,
      light: BRAND.tealLight,
      dark: BRAND.tealDark,
      contrastText: "#ffffff",
    },
    secondary: {
      main: BRAND.peach,
      light: BRAND.peachLight,
      dark: BRAND.peachDark,
      contrastText: BRAND.teal,
    },
    success: {
      main: "#2E7D3A",
    },
    background: {
      default: "#ffffff",
      paper: BRAND.offWhite,
    },
    text: {
      primary: BRAND.ink,
      secondary: BRAND.inkMuted,
    },
    grey: {
      100: BRAND.offWhite,
      300: "#EFEFEF",
      400: "#E6E6E6",
      450: "#A5A5A5",
      500: "#636363",
    },
    blue: {
      200: "#CFE3E4", // teal-tinted pale
      500: BRAND.teal,
      600: BRAND.tealDark,
    },
    green: {
      // Darkened from #60BC7C (~3.4:1) to meet AA ≥4.5:1 on white.
      // Use palette.success.main for solid badge fills; this stays for
      // outlines, soft backgrounds, and decorative chart accents.
      400: "#1F8A3F",
    },
  } satisfies PaletteOptions,
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          "&:focus-visible": {
            outline: `2px solid ${BRAND.teal}`,
            outlineOffset: 2,
          },
        },
      },
    },
  },
});

export default theme;
